import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Download,
  ExternalLink,
  FileText,
  NotebookText,
  Play,
  Plus,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import clsx from "clsx";
import {
  Library,
  Skills,
  type LibraryDocument,
  type Skill,
  type SkillInput,
} from "@/api/assistant";
import { Accounts } from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/store/auth";
import { confirmDialog, toast } from "@/lib/feedback";
import { fmtDay } from "@/lib/format";
import TopBar from "@/components/TopBar";
import Markdown from "@/components/assistant/Markdown";
import { Empty, OverflowMenu, Segmented } from "@/components/ui";

type Tab = "documents" | "skills";
type KindFilter = "all" | "pdf" | "research";

export default function LibraryPage() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "skills" ? "skills" : "documents";
  const setTab = (t: Tab) => {
    const next = new URLSearchParams(params);
    if (t === "documents") next.delete("tab");
    else next.set("tab", t);
    setParams(next, { replace: true });
  };

  return (
    <>
      <TopBar
        title="Library"
        subtitle="Documents the assistant created, and the skills it can run."
        actions={
          <Segmented<Tab>
            label="Library section"
            value={tab}
            onChange={setTab}
            options={[
              { value: "documents", label: "Documents" },
              { value: "skills", label: "Skills" },
            ]}
          />
        }
      />
      <div className="px-4 py-4 md:px-6 md:py-6 max-w-[1100px]">
        {tab === "documents" ? <DocumentsTab /> : <SkillsTab />}
      </div>
    </>
  );
}

// ─── Documents ──────────────────────────────────────────────

function DocumentsTab() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.user);
  const [params, setParams] = useSearchParams();
  const [kind, setKind] = useState<KindFilter>("all");
  const openId = params.get("doc");

  const docsQuery = useQuery({
    queryKey: ["documents", kind],
    queryFn: () => Library.list(kind === "all" ? undefined : { kind }),
  });
  const accountsQuery = useQuery({ queryKey: ["accounts"], queryFn: () => Accounts.list() });
  const accountName = (id: string | null) => accountsQuery.data?.find((a) => a.id === id)?.name;

  const remove = useMutation({
    mutationFn: (id: string) => Library.remove(id),
    onSuccess: () => {
      toast.ok("Document deleted");
      void qc.invalidateQueries({ queryKey: ["documents"] });
    },
    onError: (e) => toast.err(apiErrorMessage(e)),
  });

  const openPdf = async (d: LibraryDocument, download: boolean) => {
    try {
      await Library.openPdf(d.id, download, `${d.title}.pdf`);
    } catch (e) {
      toast.err(apiErrorMessage(e));
    }
  };

  const docs = docsQuery.data ?? [];
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <Segmented<KindFilter>
          label="Document type"
          value={kind}
          onChange={setKind}
          options={[
            { value: "all", label: "All" },
            { value: "pdf", label: "PDFs" },
            { value: "research", label: "Research" },
          ]}
        />
        <button className="btn btn-primary" onClick={() => navigate("/?skill=partner-brief")}>
          <Plus className="w-3.5 h-3.5" /> New <span className="hidden sm:inline">partnership</span>{" "}
          brief
        </button>
      </div>

      <div className="panel divide-y divide-fg/[0.06]">
        {docsQuery.isLoading ? (
          <div className="p-6 text-mut-500 text-md">Loading…</div>
        ) : docs.length === 0 ? (
          <Empty title="Nothing here yet">
            Ask the assistant for a partnership brief or company research, and the result lands
            here.
          </Empty>
        ) : (
          docs.map((d) => {
            const isPdf = d.kind === "pdf";
            const Icon = isPdf ? FileText : NotebookText;
            const account = accountName(d.account_id);
            return (
              <div key={d.id} className="flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3.5">
                <span
                  className="grid place-items-center w-10 h-10 rounded-xl shrink-0 text-white"
                  style={{ background: isPdf ? "var(--grad)" : "rgb(var(--cyan))" }}
                >
                  <Icon className="w-[18px] h-[18px]" />
                </span>
                <button
                  className="flex-1 min-w-0 text-left"
                  onClick={() => (isPdf ? void openPdf(d, false) : setParams({ doc: d.id }))}
                >
                  <div className="text-md font-semibold truncate">{d.title}</div>
                  <div className="text-sm text-mut-500 truncate">
                    {isPdf ? "PDF" : "Research note"}
                    {d.company ? ` · ${d.company}` : ""}
                    {account ? ` · filed under ${account}` : ""} · {fmtDay(d.created_at)}
                  </div>
                </button>
                <div className="flex items-center gap-0 sm:gap-1 shrink-0">
                  {isPdf ? (
                    <>
                      <button
                        className="btn btn-ghost"
                        aria-label={`Open ${d.title}`}
                        onClick={() => void openPdf(d, false)}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />{" "}
                        <span className="hidden sm:inline">Open</span>
                      </button>
                      <button
                        className="btn btn-ghost !px-2"
                        aria-label={`Download ${d.title}`}
                        onClick={() => void openPdf(d, true)}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : (
                    <button className="btn btn-ghost" onClick={() => setParams({ doc: d.id })}>
                      Read
                    </button>
                  )}
                  {(d.created_by === me?.id || me?.role === "admin") && (
                    <OverflowMenu
                      items={[
                        {
                          label: "Delete",
                          danger: true,
                          icon: <Trash2 className="w-3.5 h-3.5" />,
                          onClick: async () => {
                            const ok = await confirmDialog({
                              title: "Delete this document?",
                              body: `“${d.title}” will be removed from the Library for everyone.`,
                              confirmLabel: "Delete",
                              danger: true,
                            });
                            if (ok) remove.mutate(d.id);
                          },
                        },
                      ]}
                    />
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {openId && <ResearchReader id={openId} onClose={() => setParams({})} />}
    </div>
  );
}

function ResearchReader({ id, onClose }: { id: string; onClose: () => void }) {
  const docQuery = useQuery({ queryKey: ["document", id], queryFn: () => Library.get(id) });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const c = (docQuery.data?.content ?? {}) as {
    summary?: string;
    body_markdown?: string;
    sources?: { title: string; url: string }[];
  };
  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div className="overlay-backdrop" onClick={onClose} />
      <aside className="relative h-full w-[min(760px,96vw)] bg-ink-900 shadow-soft animate-slide-in overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-4 md:px-6 py-4 bg-ink-900/95 backdrop-blur border-b border-fg/[0.06]">
          <div className="min-w-0">
            <div className="section-title">Research note</div>
            <h2 className="text-lg font-semibold truncate">{docQuery.data?.title ?? "…"}</h2>
          </div>
          <button className="btn btn-ghost !px-2" aria-label="Close" onClick={onClose}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-4 py-4 md:px-6 md:py-6 space-y-6">
          {docQuery.isLoading && <div className="text-mut-500">Loading…</div>}
          {c.summary && <p className="text-base text-mut-200">{c.summary}</p>}
          {c.body_markdown && <Markdown text={c.body_markdown} />}
          {c.sources && c.sources.length > 0 && (
            <div>
              <div className="section-title mb-2">Sources</div>
              <ol className="list-decimal pl-5 space-y-1 text-sm">
                {c.sources.map((s, i) => (
                  <li key={i}>
                    <a
                      className="text-accent-azure hover:underline break-all"
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {s.title || s.url}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

// ─── Skills ─────────────────────────────────────────────────

const OUTPUT_LABEL: Record<Skill["output"], string> = {
  chat: "Answers in chat",
  pdf: "Makes a PDF",
  research: "Saves research",
};
const OUTPUT_ROLE: Record<Skill["output"], string> = {
  chat: "azure",
  pdf: "violet",
  research: "cyan",
};

function SkillsTab() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.user);
  const [editing, setEditing] = useState<Skill | "new" | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const skillsQuery = useQuery({ queryKey: ["skills"], queryFn: Skills.list });

  const refresh = () => void qc.invalidateQueries({ queryKey: ["skills"] });

  const importFile = async (file: File) => {
    try {
      const skill = await Skills.importMarkdown(await file.text());
      toast.ok(`Imported /${skill.name}`);
      refresh();
    } catch (e) {
      toast.err(`Couldn't import: ${apiErrorMessage(e)}`);
    }
  };

  const exportSkill = async (s: Skill) => {
    try {
      const md = await Skills.exportMarkdown(s.id);
      const url = URL.createObjectURL(new Blob([md], { type: "text/markdown" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${s.name}.SKILL.md`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.err(apiErrorMessage(e));
    }
  };

  const removeSkill = async (s: Skill) => {
    const ok = await confirmDialog({
      title: `Delete /${s.name}?`,
      body: "Nobody in your organisation will be able to run it any more.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await Skills.remove(s.id);
      toast.ok("Skill deleted");
      refresh();
    } catch (e) {
      toast.err(apiErrorMessage(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-md text-mut-300 max-w-[60ch]">
          A skill is a saved set of instructions. Type{" "}
          <span className="font-mono text-accent-violet">/name</span> in the chat to run one. Skills
          are shared with your whole organisation.
        </p>
        <div className="flex gap-2 shrink-0">
          <button className="btn" onClick={() => fileRef.current?.click()}>
            <Upload className="w-3.5 h-3.5" /> Import
          </button>
          <button className="btn btn-primary" onClick={() => setEditing("new")}>
            <Plus className="w-3.5 h-3.5" /> New skill
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".md,.markdown,text/markdown,text/plain"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void importFile(f);
              e.target.value = "";
            }}
          />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {(skillsQuery.data ?? []).map((s) => (
          <div key={s.id} className="panel p-4 flex flex-col gap-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-sm text-accent-violet">/{s.name}</div>
                <div className="text-base font-semibold mt-0.5">{s.title}</div>
              </div>
              <span
                className="chip-tint shrink-0"
                style={{ ["--c" as string]: `var(--${OUTPUT_ROLE[s.output]})` }}
              >
                {OUTPUT_LABEL[s.output]}
              </span>
            </div>
            <p className="text-md text-mut-300 flex-1">{s.description}</p>
            <div className="flex items-center gap-1.5">
              <button className="btn" onClick={() => navigate(`/?skill=${s.name}`)}>
                <Play className="w-3.5 h-3.5" /> Run
              </button>
              <button className="btn btn-ghost" onClick={() => setEditing(s)}>
                Edit
              </button>
              <div className="flex-1" />
              {s.is_builtin && <span className="text-xs text-mut-500">Built in</span>}
              <OverflowMenu
                items={[
                  {
                    label: "Export as SKILL.md",
                    icon: <Download className="w-3.5 h-3.5" />,
                    onClick: () => void exportSkill(s),
                  },
                  ...(!s.is_builtin && me
                    ? [
                        {
                          label: "Delete",
                          danger: true,
                          icon: <Trash2 className="w-3.5 h-3.5" />,
                          onClick: () => void removeSkill(s),
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <SkillEditor
          skill={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function SkillEditor({
  skill,
  onClose,
  onSaved,
}: {
  skill: Skill | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<SkillInput>(
    skill
      ? {
          name: skill.name,
          title: skill.title,
          description: skill.description,
          instructions: skill.instructions,
          output: skill.output,
        }
      : { name: "", title: "", description: "", instructions: "", output: "chat" },
  );
  const save = useMutation({
    mutationFn: () =>
      skill
        ? Skills.update(skill.id, {
            title: form.title,
            description: form.description,
            instructions: form.instructions,
            output: form.output,
          })
        : Skills.create(form),
    onSuccess: (s) => {
      toast.ok(skill ? "Skill saved" : `Created /${s.name}`);
      onSaved();
    },
    onError: (e) => toast.err(apiErrorMessage(e)),
  });
  const valid =
    /^[a-z0-9][a-z0-9-]{1,58}$/.test(form.name) &&
    form.title.trim() &&
    form.description.trim() &&
    form.instructions.trim();

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center p-4">
      <div className="overlay-backdrop" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="skill-editor-title"
        className="panel relative w-[min(760px,100%)] max-h-[92vh] overflow-y-auto p-6 animate-fade-up"
      >
        <div className="flex items-center justify-between mb-5">
          <h2 id="skill-editor-title" className="text-lg font-semibold">
            {skill ? `Edit /${skill.name}` : "New skill"}
          </h2>
          <button className="btn btn-ghost !px-2" aria-label="Close" onClick={onClose}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="skill-name">
                Name (what you type after /)
              </label>
              <input
                id="skill-name"
                className="input font-mono"
                value={form.name}
                disabled={!!skill}
                placeholder="board-pack"
                onChange={(e) =>
                  setForm({
                    ...form,
                    name: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
                  })
                }
              />
            </div>
            <div>
              <label className="label" htmlFor="skill-title">
                Title
              </label>
              <input
                id="skill-title"
                className="input"
                value={form.title}
                placeholder="Board pack PDF"
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="label" htmlFor="skill-description">
              One-line description
            </label>
            <input
              id="skill-description"
              className="input"
              value={form.description}
              placeholder="What it does, in one sentence"
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div>
            <span className="label">What it produces</span>
            <Segmented<Skill["output"]>
              label="Output"
              value={form.output}
              onChange={(output) => setForm({ ...form, output })}
              options={[
                { value: "chat", label: "An answer" },
                { value: "pdf", label: "A PDF" },
                { value: "research", label: "Saved research" },
              ]}
            />
          </div>
          <div>
            <label className="label" htmlFor="skill-instructions">
              Instructions
            </label>
            <textarea
              id="skill-instructions"
              className={clsx("input font-mono text-sm leading-6 min-h-[300px]")}
              value={form.instructions}
              placeholder={"Goal: …\n\nSteps\n1. …\n2. …"}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
            />
            <p className="mt-1.5 text-xs text-mut-500">
              Write it like a brief for a sharp colleague: the goal, the steps, what good looks
              like. The assistant can search the CRM, research the web, propose CRM changes, and
              create PDFs or research notes.
            </p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={!valid || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Saving…" : skill ? "Save" : "Create skill"}
          </button>
        </div>
      </div>
    </div>
  );
}
